import React, { useState, useMemo, useEffect } from "react";
import { AxisLeft, AxisRight, AxisBottom } from "@visx/axis";
import { Group } from "@visx/group";
import { Bar } from "@visx/shape";
import { scaleBand, scaleLinear } from "@visx/scale";
import { LinePath, Line } from "@visx/shape";
import { curveLinear } from "@visx/curve";
import { localPoint } from "@visx/event";
import { ParentSize } from "@visx/responsive";
import "./InsightsGraph.css";
import CustomDropdown from "../dropdown/CustomDropDown";
import { IonIcon } from "@ionic/react";
import { chevronForward } from "ionicons/icons";
import { formatDateInsights, formatDate } from "../../utils/date";
import { getResponsiveLayout } from "../../utils/helpers";

interface InsightsGraphProps {
  profileId?: number;
  insightsData: {
    therapies: Array<{
      date: string;
      breathing_treatment: number;
      vest_treatment: number;
      biwaze_cough: number;
      biwaze_clear: number;
    }>;
    vitals: {
      spo2: Array<{ date: string; values: number[] }>;
      heart_rate: Array<{ date: string; values: number[] }>;
      aqi: Array<{ date: string; values: number[] }>;
    };
  };
  isMockData?: boolean;
  isCurrentUser?: boolean;
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  className?: string;
  onClick?: () => void;
  showGraphs?: string[];
  title?: string;
}

// Constants
const LEGEND_ITEMS = [
  { label: "Cough Therapies", color: "#FFB84D", key: "biwaze_cough" },
  { label: "Clear Therapies", color: "#64D2FF", key: "biwaze_clear" },
  { label: "Breathing Routines", color: "#4CAF50", key: "breathing_treatment" },
  { label: "Vest Treatments", color: "#1E5F8C", key: "vest_treatment" },
] as const;

const VITAL_LEGEND_ITEMS = [
  { label: "Median", type: "dash" as const },
  { label: "Max", type: "filled-diamond" as const },
  { label: "Min", type: "hollow-diamond" as const },
] as const;

const THERAPY_TRACES_CONFIG = [
  { name: "Clear", field: "biwaze_clear", color: "#64D2FF" },
  { name: "Cough", field: "biwaze_cough", color: "#FFB84D" },
  { name: "Vest", field: "vest_treatment", color: "#1E5F8C" },
  { name: "Breathing", field: "breathing_treatment", color: "#4CAF50" },
] as const;

const METRIC_CONFIG = {
  spo2: { label: "SpO2 (%)", dropdownLabel: "SpO2" },
  heart_rate: { label: "Heart Rate (BPM)", dropdownLabel: "Heart Rate" },
  aqi: { label: "Air Quality Index", dropdownLabel: "AQI" },
} as const;

// Format full date for tooltip display using existing utility
const formatFullDate = (dateStr: string, dataLength: number) => {
  // Parse date components directly from string to avoid timezone shift
  const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);

  if (dataLength === 12) {
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    return `${months[month - 1]} ${year}`;
  }

  // Use existing formatDate utility for MMM DD, YYYY format
  const date = new Date(year, month - 1, day);
  return formatDate(date);
};

const InsightsGraphVisx: React.FC<InsightsGraphProps> = ({
  insightsData,
  dateRange,
  onClick,
  isCurrentUser = true,
  isMockData = false,
  className = "m-4",
  showGraphs = [
    "biwaze_cough",
    "biwaze_clear",
    "breathing_treatment",
    "vest_treatment",
  ],
  title = "Respiratory Therapy Overview",
}) => {
  const [selectedMetric, setSelectedMetric] = useState<
    "spo2" | "heart_rate" | "aqi"
  >("spo2");

  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const [isLargeScreen, setIsLargeScreen] = useState(
    typeof window !== "undefined" ? window.innerWidth > 480 : false
  );

  useEffect(() => {
    const handleResize = () => setIsLargeScreen(window.innerWidth > 480);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const dataLengthInfo = useMemo(
    () => ({
      moreThan7Days: insightsData.therapies.length > 7,
      yearlyData: insightsData.therapies.length === 12,
    }),
    [insightsData.therapies.length]
  );

  // Calculate statistics for vital values
  const calculateStats = (values: number[]) => {
    if (values.length === 0)
      return { min: null, max: null, median: null, mean: null };

    const sorted = [...values].sort((a, b) => a - b);
    const min = sorted[0];
    const max = sorted[sorted.length - 1];
    const median =
      sorted.length % 2 === 0
        ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
        : sorted[Math.floor(sorted.length / 2)];
    const mean = Number(
      (sorted.reduce((sum, v) => sum + v, 0) / sorted.length).toFixed(2)
    );

    return { min, max, median, mean };
  };

  // Get formatted dates for x-axis
  const formattedDates = useMemo(
    () =>
      insightsData.therapies.map((therapy, idx) =>
        formatDateInsights(
          therapy.date,
          idx,
          insightsData.therapies,
          dataLengthInfo
        )
      ),
    [insightsData.therapies, dataLengthInfo]
  );

  // Prepare therapy data
  const therapyData = useMemo(() => {
    return formattedDates.map((date, idx) => ({
      ...insightsData.therapies[idx],
      date,
    }));
  }, [formattedDates, insightsData.therapies]);

  // Prepare vital statistics
  const vitalStats = useMemo(() => {
    const vitalData = insightsData.vitals[selectedMetric];
    return (vitalData || [])
      .map((day) => ({
        date: day.date,
        ...calculateStats(day.values),
      }))
      .filter((s) => s.min !== null);
  }, [insightsData.vitals, selectedMetric]);

  // Map vital dates to formatted dates
  const vitalStatsWithFormattedDates = useMemo(() => {
    return vitalStats.map((stat) => {
      const therapyIndex = insightsData.therapies.findIndex(
        (t) => t.date === stat.date
      );
      const formattedDate =
        therapyIndex >= 0
          ? formattedDates[therapyIndex]
          : formatDateInsights(
              stat.date,
              0,
              insightsData.therapies,
              dataLengthInfo
            );
      return {
        ...stat,
        formattedDate,
      };
    });
  }, [vitalStats, insightsData.therapies, formattedDates, dataLengthInfo]);

  // Calculate y-axis range for vitals
  const vitalRange = useMemo(() => {
    const vitalData = insightsData.vitals[selectedMetric];
    const allValues = vitalData?.flatMap((day) => day.values);

    if (!allValues || allValues.length === 0) {
      switch (selectedMetric) {
        case "spo2":
          return [80, 110];
        case "heart_rate":
          return [50, 140];
        case "aqi":
          return [0, 5];
        default:
          return [0, 100];
      }
    }

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);

    switch (selectedMetric) {
      case "spo2":
        return [Math.min(minValue - 10, 70), Math.max(maxValue + 10, 110)];
      case "heart_rate":
        return [Math.min(minValue - 10, 50), Math.max(maxValue + 10, 100)];
      case "aqi":
        return [0, Math.max(maxValue + 1, 5)];
      default:
        return [
          minValue - (maxValue - minValue) * 0.1,
          maxValue + (maxValue - minValue) * 0.1,
        ];
    }
  }, [insightsData.vitals, selectedMetric]);

  // Calculate max therapy value for y-axis
  const maxTherapyValue = useMemo(() => {
    return insightsData.therapies.reduce(
      (max, t) =>
        Math.max(
          max,
          ...showGraphs.map((key) => Number(t[key as keyof typeof t]) || 0)
        ),
      0
    );
  }, [insightsData.therapies, showGraphs]);

  // Filter legend items
  const visibleLegendItems = LEGEND_ITEMS.filter((item) => {
    if (!showGraphs.includes(item.key)) return false;
    if (showGraphs.includes(item.key) && showGraphs.length === 1) return true;
    return insightsData.therapies.some(
      (t) =>
        t[item.key as keyof typeof t] && t[item.key as keyof typeof t] !== 0
    );
  });

  // Filtered therapy configs
  const visibleTherapyConfigs = THERAPY_TRACES_CONFIG.filter(({ field }) =>
    showGraphs.includes(field)
  );

  // Initialize activeIndex to last date on mount
  useEffect(() => {
    if (therapyData.length > 0 && activeIndex === null) {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(
        today.getMonth() + 1
      ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      const todayIndex = insightsData.therapies.findIndex(
        (item) => item.date === todayStr
      );

      const currentMonth = today.toLocaleString("en-US", { month: "short" });
      const monthIndex = therapyData.findIndex(
        (item) => item.date === currentMonth
      );
      // console.log("todayIndex", todayStr, todayIndex, therapyData);

      setActiveIndex(
        todayIndex !== -1
          ? todayIndex
          : monthIndex !== -1
          ? monthIndex
          : therapyData.length - 1
      );
    }
  }, [therapyData.length, activeIndex]);

  useEffect(() => {
    console.log("insightsdata", insightsData);
  }, [insightsData]);

  // Get active data for centralized tooltip
  const activeData = useMemo(() => {
    if (activeIndex === null || !therapyData[activeIndex]) return null;

    const therapy = therapyData[activeIndex];
    const originalDate = insightsData.therapies[activeIndex].date;
    const vitalStat = vitalStatsWithFormattedDates.find(
      (v) => v.formattedDate === therapy.date
    );

    return {
      date: formatFullDate(originalDate, insightsData.therapies.length),
      therapies: THERAPY_TRACES_CONFIG.filter((config) =>
        showGraphs.includes(config.field)
      ).map((config) => ({
        name: config.name,
        value: therapy[config.field] || 0,
        color: config.color,
      })),
      vital: vitalStat
        ? {
            label: METRIC_CONFIG[selectedMetric].dropdownLabel,
            median: vitalStat.median,
            mean: vitalStat.mean,
            min: vitalStat.min,
            max: vitalStat.max,
          }
        : null,
    };
  }, [
    activeIndex,
    therapyData,
    vitalStatsWithFormattedDates,
    selectedMetric,
    showGraphs,
    insightsData.therapies,
  ]);
  const responsive = getResponsiveLayout(
    typeof window !== "undefined" ? window.innerWidth : 0
  );

  return (
    <div className={`insights-card bg-white shadow-md rounded-xl ${className}`}>
      <div className="!px-4 !pt-2 !pb-2 biwaze-summary-title">
        <div className="patient-avatar-name">
          <h2 className="patient-name">{title}</h2>
        </div>
        {onClick && (
          <div className="biwaze-arrow clickable" onClick={onClick}>
            <IonIcon icon={chevronForward} className="w-6 h-6" />
          </div>
        )}
      </div>

      {(!isMockData || (isMockData && isCurrentUser)) && (
        <div className="px-4" style={{ display: "none" }}>
          {/* First Row: Therapy Legend and Dropdown - HIDDEN */}
          <div className="flex mb-2">
            {/* Therapy Legend */}
            <div>
              {visibleLegendItems.map((item) => (
                <div
                  className={`legend-item flex items-center ${
                    isLargeScreen ? "text-base" : "text-xs"
                  }`}
                  key={item.label}
                >
                  <span
                    className="legend-color"
                    style={{
                      backgroundColor: item.color,
                      width: isLargeScreen ? 16 : 10,
                      height: isLargeScreen ? 16 : 10,
                      borderRadius: 3,
                      display: "inline-block",
                      marginRight: isLargeScreen ? 8 : 4,
                    }}
                  />
                  <span className="legend-label">{item.label}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col justify-end items-end self-stretch ml-auto">
              <span className="text-sm text-gray-500 mb-1">
                Metric to trend
              </span>
              <CustomDropdown
                options={Object.entries(METRIC_CONFIG).map(([key, config]) => ({
                  label: config.dropdownLabel,
                  value: key,
                }))}
                selected={selectedMetric}
                onChange={(value: string) =>
                  setSelectedMetric(value as keyof typeof METRIC_CONFIG)
                }
                placeholder="Select..."
                minWidth={responsive.dropdownWidth}
              />
            </div>
          </div>

          {/* Second Row: Vital Stats Legend - HIDDEN */}
          <div
            className={`flex gap-${
              isLargeScreen ? "4" : "2"
            } items-center justify-end`}
          >
            {VITAL_LEGEND_ITEMS.map((item) => (
              <div
                className={`legend-item flex items-center ${
                  isLargeScreen ? "text-base" : "text-xs"
                }`}
                key={item.label}
              >
                <span
                  className="legend-icon"
                  style={{
                    width: isLargeScreen ? 16 : 10,
                    height: isLargeScreen ? 16 : 10,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: isLargeScreen ? 8 : 4,
                  }}
                >
                  {item.type === "dash" && (
                    <svg
                      width={isLargeScreen ? 16 : 10}
                      height={isLargeScreen ? 16 : 10}
                      viewBox="0 0 16 16"
                    >
                      <line
                        x1="2"
                        y1="8"
                        x2="14"
                        y2="8"
                        stroke="#000"
                        strokeWidth="2"
                      />
                    </svg>
                  )}
                  {item.type === "filled-diamond" && (
                    <svg
                      width={isLargeScreen ? 16 : 10}
                      height={isLargeScreen ? 16 : 10}
                      viewBox="0 0 16 16"
                    >
                      <path d="M 8,2 L 14,8 L 8,14 L 2,8 Z" fill="#000" />
                    </svg>
                  )}
                  {item.type === "hollow-diamond" && (
                    <svg
                      width={isLargeScreen ? 16 : 10}
                      height={isLargeScreen ? 16 : 10}
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M 8,2 L 14,8 L 8,14 L 2,8 Z"
                        fill="none"
                        stroke="#000"
                        strokeWidth="1.5"
                      />
                    </svg>
                  )}
                </span>
                <span className="legend-label" style={{ color: "#000" }}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        {isCurrentUser || !isMockData ? (
          <>
            {/* Centralized Tooltip */}
            {(!isMockData || (isMockData && isCurrentUser)) && (
              <div className="px-4 mb-2">
                <div className="flex flex-col lg:flex-row lg:justify-between items-stretch lg:items-start gap-4">
                  {/* Active Data Tooltip - Left on small, Center on large */}
                  {activeData && (
                    <div className="bg-gray-50 rounded-lg shadow-md px-4 py-2 w-fit lg:mx-auto">
                      <div className="text-left text-xs font-semibold text-gray-700 mb-2">
                        {activeData.date}
                      </div>

                      <div className="flex md:gap-6 flex-col md:flex-row">
                        {/* Therapies */}
                        <div className="flex flex-col gap-1">
                          {activeData.therapies.filter(
                            (t) => t.name === "Cough" || t.name === "Clear"
                          ).length > 0 && (
                            <div className="flex justify-start items-center gap-3">
                              {activeData.therapies
                                .filter(
                                  (t) =>
                                    t.name === "Cough" || t.name === "Clear"
                                )
                                .map((therapy) => (
                                  <div
                                    key={therapy.name}
                                    className="flex items-center gap-1"
                                  >
                                    <span
                                      className="w-2.5 h-2.5 rounded-sm"
                                      style={{ backgroundColor: therapy.color }}
                                    />
                                    <span className="text-xs text-gray-700">
                                      {therapy.name}:
                                    </span>
                                    <span className="text-xs text-gray-900 font-medium">
                                      {therapy.value}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          )}

                          {activeData.therapies.filter(
                            (t) => t.name === "Breathing" || t.name === "Vest"
                          ).length > 0 && (
                            <div className="flex justify-start items-center gap-3">
                              {activeData.therapies
                                .filter(
                                  (t) =>
                                    t.name === "Breathing" || t.name === "Vest"
                                )
                                .map((therapy) => (
                                  <div
                                    key={therapy.name}
                                    className="flex items-center gap-1"
                                  >
                                    <span
                                      className="w-2.5 h-2.5 rounded-sm"
                                      style={{ backgroundColor: therapy.color }}
                                    />
                                    <span className="text-xs text-gray-700">
                                      {therapy.name}:
                                    </span>
                                    <span className="text-xs text-gray-900 font-medium">
                                      {therapy.value}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>

                        {/* Metric */}
                        <div className="flex flex-col gap-1 md:border-l border-gray-200 md:pl-6 mt-2 md:mt-0">
                          <div className="flex justify-start items-center gap-2">
                            <span className="text-xs font-medium text-gray-500">
                              {activeData.vital?.label
                                ? `${activeData.vital.label} (Mean):`
                                : "Metric:"}
                            </span>
                            {activeData.vital ? (
                              <span className="text-xs text-gray-700">
                                {activeData.vital.mean?.toFixed(1)}{" "}
                                <span className="text-gray-500">
                                  ({activeData.vital.min?.toFixed(1)} -{" "}
                                  {activeData.vital.max?.toFixed(1)})
                                </span>
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">
                                No data
                              </span>
                            )}
                          </div>

                          <div className="flex justify-start gap-3 pt-1 border-t border-gray-200">
                            <div className="flex items-center gap-1">
                              <svg width="12" height="12" viewBox="0 0 16 16">
                                <line
                                  x1="2"
                                  y1="8"
                                  x2="14"
                                  y2="8"
                                  stroke="#666"
                                  strokeWidth="2"
                                />
                              </svg>
                              <span className="text-xs text-gray-500">
                                Mean
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <svg width="12" height="12" viewBox="0 0 16 16">
                                <path
                                  d="M 8,2 L 14,8 L 8,14 L 2,8 Z"
                                  fill="#666"
                                />
                              </svg>
                              <span className="text-xs text-gray-500">Max</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <svg width="12" height="12" viewBox="0 0 16 16">
                                <path
                                  d="M 8,2 L 14,8 L 8,14 L 2,8 Z"
                                  fill="none"
                                  stroke="#666"
                                  strokeWidth="1.5"
                                />
                              </svg>
                              <span className="text-xs text-gray-500">Min</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Dropdown - Always right */}
                  <div className="flex flex-col justify-end items-end lg:ml-auto">
                    <span className="text-xs font-medium text-gray-500 mb-1">
                      Metric to trend
                    </span>
                    <CustomDropdown
                      options={Object.entries(METRIC_CONFIG).map(
                        ([key, config]) => ({
                          label: config.dropdownLabel,
                          value: key,
                        })
                      )}
                      selected={selectedMetric}
                      onChange={(value: string) =>
                        setSelectedMetric(value as keyof typeof METRIC_CONFIG)
                      }
                      placeholder="Select..."
                      minWidth={responsive.dropdownWidth}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center px-4 mb-1">
              <span className="text-xs font-medium text-gray-600">
                No. of Therapies
              </span>
              <span className="text-xs font-medium text-gray-600">
                {METRIC_CONFIG[selectedMetric].label}
              </span>
            </div>

            <ParentSize>
              {({ width }) => (
                <VisxChart
                  width={width}
                  height={isLargeScreen ? 400 : 300}
                  therapyData={therapyData}
                  vitalStats={vitalStatsWithFormattedDates}
                  visibleTherapyConfigs={visibleTherapyConfigs as any}
                  maxTherapyValue={maxTherapyValue}
                  vitalRange={vitalRange as any}
                  selectedMetric={selectedMetric}
                  formattedDates={formattedDates}
                  isLargeScreen={isLargeScreen}
                  isMockData={isMockData}
                  isCurrentUser={isCurrentUser}
                  activeIndex={activeIndex}
                  setActiveIndex={setActiveIndex}
                />
              )}
            </ParentSize>
          </>
        ) : (
          <div className="p-4 text-center text-gray-500">
            <p>You are viewing another user&apos;s data.</p>
            <p>No data is available for this user.</p>
          </div>
        )}
      </div>
    </div>
  );
};

interface VisxChartProps {
  width: number;
  height: number;
  therapyData: any[];
  vitalStats: any[];
  visibleTherapyConfigs: typeof THERAPY_TRACES_CONFIG;
  maxTherapyValue: number;
  vitalRange: [number, number];
  selectedMetric: "spo2" | "heart_rate" | "aqi";
  formattedDates: string[];
  isLargeScreen: boolean;
  isMockData: boolean;
  isCurrentUser: boolean;
  activeIndex: number | null;
  setActiveIndex: (index: number | null) => void;
}

const VisxChart: React.FC<VisxChartProps> = ({
  width,
  height,
  therapyData,
  vitalStats,
  visibleTherapyConfigs,
  maxTherapyValue,
  vitalRange,
  selectedMetric,
  formattedDates,
  isLargeScreen,
  isMockData,
  isCurrentUser,
  activeIndex,
  setActiveIndex,
}) => {
  // Margins
  const margin = { top: 20, right: 40, bottom: 60, left: 40 };
  // F-636: clamped — before ParentSize has measured (width 0) this was -80, and the overlay
  // <rect width={xMax}> below rendered with a negative width.
  const xMax = Math.max(0, width - margin.left - margin.right);
  const yMax = height - margin.top - margin.bottom;

  // Scales
  const xScale = scaleBand({
    domain: formattedDates,
    range: [0, xMax],
    padding: 0.2,
  });

  const yScaleTherapy = scaleLinear({
    domain: [0, Math.max(5, maxTherapyValue)],
    range: [yMax, 0],
    nice: true,
  });

  const yScaleVital = scaleLinear({
    domain: vitalRange,
    range: [yMax, 0],
    nice: true,
  });
  const vitalTickValues = yScaleVital.ticks(5);
  const therapyTickValues = yScaleTherapy.ticks(5).map((t) => Math.round(t));

  // Calculate bar width for grouped bars
  const groupWidth = xScale.bandwidth();
  const barWidth = groupWidth / visibleTherapyConfigs.length;

  // Handle pointer interaction
  const handleChartInteraction = (
    event: React.PointerEvent<SVGRectElement>
  ) => {
    const point = localPoint(event);
    if (!point) return;

    const x = point.x - margin.left;

    // Find nearest date
    let nearestIndex = 0;
    let minDistance = Infinity;

    formattedDates.forEach((date, idx) => {
      const bandX = xScale(date);
      if (bandX !== undefined) {
        const centerX = bandX + xScale.bandwidth() / 2;
        const distance = Math.abs(x - centerX);
        if (distance < minDistance) {
          minDistance = distance;
          nearestIndex = idx;
        }
      }
    });

    setActiveIndex(nearestIndex);
  };

  // Diamond path helper
  const createDiamond = (cx: number, cy: number, size: number) => {
    return `M ${cx},${cy - size} L ${cx + size},${cy} L ${cx},${cy + size} L ${
      cx - size
    },${cy} Z`;
  };

  return (
    <div style={{ position: "relative" }}>
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
          {/* Grid lines for vital axis */}
          {yScaleVital.ticks(5).map((tick, i) => (
            <Line
              key={`grid-${i}`}
              from={{ x: 0, y: yScaleVital(tick) }}
              to={{ x: xMax, y: yScaleVital(tick) }}
              stroke="#f0f0f0"
              strokeWidth={1}
            />
          ))}

          {/* Therapy bars */}
          {therapyData.map((d, dateIdx) => {
            const x = xScale(d.date) || 0;
            return visibleTherapyConfigs.map((config, configIdx) => {
              const value = d[config.field];
              if (!value) return null;

              const barX = x + configIdx * barWidth;
              const barHeight = yMax - yScaleTherapy(value);

              return (
                <Bar
                  key={`bar-${dateIdx}-${config.field}`}
                  x={barX}
                  y={yScaleTherapy(value)}
                  width={barWidth}
                  height={barHeight}
                  fill={config.color}
                />
              );
            });
          })}

          {/* Vital min-max lines */}
          {vitalStats.map((stat, idx) => {
            const x = xScale(stat.formattedDate);
            if (!x || stat.min === null || stat.max === null) return null;

            const centerX = x + xScale.bandwidth() / 2;
            return (
              <Line
                key={`vital-line-${idx}`}
                from={{ x: centerX, y: yScaleVital(stat.min) }}
                to={{ x: centerX, y: yScaleVital(stat.max) }}
                stroke="#FF6347"
                strokeWidth={1}
              />
            );
          })}

          {/* Vital mean line */}
          {vitalStats.length > 0 && (
            <LinePath
              data={vitalStats}
              x={(d) => {
                const x = xScale(d.formattedDate);
                return x ? x + xScale.bandwidth() / 2 : 0;
              }}
              y={(d) => yScaleVital(d.mean)}
              stroke="#FF6347"
              strokeWidth={1}
              strokeDasharray="4,4"
              curve={curveLinear}
            />
          )}

          {/* Vital mean markers */}
          {vitalStats.map((stat, idx) => {
            const x = xScale(stat.formattedDate);
            if (!x || stat.mean === null) return null;

            const centerX = x + xScale.bandwidth() / 2;
            const y = yScaleVital(stat.mean);
            const dashWidth = xScale.bandwidth() * 0.8;
            const dashHalfWidth = dashWidth / 2;

            return (
              <g key={`marker-${idx}`}>
                {/* Horizontal dash for mean */}
                <line
                  x1={centerX - dashHalfWidth}
                  y1={y}
                  x2={centerX + dashHalfWidth}
                  y2={y}
                  stroke="#FF6347"
                  strokeWidth={2}
                />
                {/* Max diamond (filled) */}
                <path
                  d={createDiamond(centerX, yScaleVital(stat.max), 4)}
                  fill="#FF6347"
                  stroke="none"
                />
                {/* Min diamond (hollow) */}
                <path
                  d={createDiamond(centerX, yScaleVital(stat.min), 4)}
                  fill="none"
                  stroke="#FF6347"
                  strokeWidth={1.5}
                />
              </g>
            );
          })}

          {/* Cursor line */}
          {activeIndex !== null && therapyData[activeIndex] && (
            <>
              {/* Vertical cursor line */}
              <Line
                from={{
                  x:
                    (xScale(therapyData[activeIndex].date) || 0) +
                    xScale.bandwidth() / 2,
                  y: 10,
                }}
                to={{
                  x:
                    (xScale(therapyData[activeIndex].date) || 0) +
                    xScale.bandwidth() / 2,
                  y: yMax,
                }}
                stroke="#000"
                strokeWidth={2}
                strokeDasharray="4,4"
                pointerEvents="none"
              />

              {/* Triangular head */}
              <polygon
                points={`
                  ${
                    (xScale(therapyData[activeIndex].date) || 0) +
                    xScale.bandwidth() / 2
                  },10
                  ${
                    (xScale(therapyData[activeIndex].date) || 0) +
                    xScale.bandwidth() / 2 -
                    8
                  },0
                  ${
                    (xScale(therapyData[activeIndex].date) || 0) +
                    xScale.bandwidth() / 2 +
                    8
                  },0
                `}
                fill="#000"
                pointerEvents="none"
              />
            </>
          )}

          {/* Interaction overlay */}
          <rect
            width={xMax}
            height={yMax}
            fill="transparent"
            onPointerDown={handleChartInteraction}
            onPointerMove={handleChartInteraction}
            style={{ cursor: "pointer" }}
          />

          {/* Axes */}
          <AxisBottom
            top={yMax}
            scale={xScale}
            stroke="#4A4A4A"
            tickStroke="#4A4A4A"
            hideAxisLine={false}
            hideTicks={false}
            tickLength={0}
            tickLabelProps={() => ({
              fill: "#4A4A4A",
              fontSize: isLargeScreen ? 16 : 12,
              textAnchor: "middle",
            })}
          />
          <AxisLeft
            scale={yScaleTherapy}
            tickValues={therapyTickValues}
            stroke="#4A4A4A"
            tickStroke="#4A4A4A"
            hideAxisLine={false}
            hideTicks={false}
            tickLength={0}
            tickLabelProps={() => ({
              fill: "#4A4A4A",
              fontSize: isLargeScreen ? 16 : 12,
              textAnchor: "end",
              dx: -8,
            })}
          />
          <AxisRight
            left={xMax}
            scale={yScaleVital}
            tickValues={vitalTickValues}
            stroke="#4A4A4A"
            tickStroke="#4A4A4A"
            hideAxisLine={false}
            hideTicks={false}
            tickLength={0}
            tickLabelProps={() => ({
              fill: "#4A4A4A",
              fontSize: isLargeScreen ? 16 : 12,
              textAnchor: "start",
              dx: 8,
            })}
          />

          {/* Mock data watermark */}
          {isMockData && isCurrentUser && (
            <text
              x={xMax / 2}
              y={yMax / 2}
              textAnchor="middle"
              fill="rgba(0,0,0,0.15)"
              fontSize={32}
              fontWeight="bold"
              fontFamily="Inter, sans-serif"
            >
              Sample Data
            </text>
          )}
        </Group>
      </svg>
    </div>
  );
};

export default InsightsGraphVisx;
